<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center flex-wrap ga-2">
          <div>
            <h1 class="text-h4 text-primary mb-1">
              <v-icon class="mr-2">mdi-bug-outline</v-icon>
              {{ $t('admin.errorLogs') }}
            </h1>
            <p class="text-subtitle-1 text-grey">
              {{ $t('admin.errorLogsDescription') }}
            </p>
          </div>
          <div class="d-flex ga-2 flex-wrap">
            <v-btn 
              color="success" 
              variant="tonal" 
              @click="resolveSelected"
              :disabled="selectedErrors.length === 0"
            >
              <v-icon left>mdi-check-all</v-icon>
              {{ $t('admin.resolveSelected') }} ({{ selectedErrors.length }})
            </v-btn>
            <v-btn color="error" variant="tonal" @click="showClearAllDialog = true">
              <v-icon left>mdi-delete-sweep</v-icon>
              {{ $t('admin.clearAll') }}
            </v-btn>
            <v-btn color="primary" variant="tonal" @click="loadErrors">
              <v-icon left>mdi-refresh</v-icon>
              {{ $t('common.refresh') }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4" color="error" variant="tonal">
          <div class="d-flex align-center">
            <v-avatar color="error" class="mr-3">
              <v-icon>mdi-alert-circle</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.unresolved }}</div>
              <div class="text-caption">{{ $t('admin.unresolvedErrors') }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4" color="warning" variant="tonal">
          <div class="d-flex align-center">
            <v-avatar color="warning" class="mr-3">
              <v-icon>mdi-clock-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.today }}</div>
              <div class="text-caption">{{ $t('admin.todayErrors') }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4" color="info" variant="tonal">
          <div class="d-flex align-center">
            <v-avatar color="info" class="mr-3">
              <v-icon>mdi-calendar-week</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.thisWeek }}</div>
              <div class="text-caption">{{ $t('admin.weekErrors') }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4" color="success" variant="tonal">
          <div class="d-flex align-center">
            <v-avatar color="success" class="mr-3">
              <v-icon>mdi-chart-line</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.total }}</div>
              <div class="text-caption">{{ $t('admin.totalErrors') }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-4">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.errorType"
              :items="errorTypes"
              :label="$t('admin.errorType')"
              clearable
              density="compact"
              variant="outlined"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.resolved"
              :items="resolvedOptions"
              :label="$t('admin.status')"
              clearable
              density="compact"
              variant="outlined"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.search"
              :label="$t('common.search')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="outlined"
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn color="primary" block @click="loadErrors">
              {{ $t('common.filter') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Error Table -->
    <v-card>
      <v-data-table
        v-model="selectedErrors"
        :headers="headers"
        :items="errors"
        :loading="loading"
        :items-per-page="pagination.limit"
        show-select
        item-value="id"
        class="elevation-0"
      >
        <!-- Error Type -->
        <template v-slot:item.errorType="{ item }">
          <v-chip :color="getErrorTypeColor(item.errorType)" size="small" variant="tonal">
            <v-icon start size="small">{{ getErrorTypeIcon(item.errorType) }}</v-icon>
            {{ item.errorType }}
          </v-chip>
        </template>

        <!-- User -->
        <template v-slot:item.user="{ item }">
          <div v-if="item.userName || item.userPhone">
            <div class="font-weight-medium">{{ item.userName || 'Unknown' }}</div>
            <div class="text-caption text-grey">{{ item.userPhone || '' }}</div>
          </div>
          <span v-else class="text-grey">—</span>
        </template>

        <!-- Message -->
        <template v-slot:item.message="{ item }">
          <div class="text-truncate" style="max-width: 300px;" :title="item.message">
            {{ item.message }}
          </div>
        </template>

        <!-- URL -->
        <template v-slot:item.url="{ item }">
          <div class="text-truncate" style="max-width: 200px;" :title="item.url">
            {{ item.url || '—' }}
          </div>
        </template>

        <!-- Status -->
        <template v-slot:item.resolved="{ item }">
          <v-chip 
            :color="item.resolved ? 'success' : 'error'" 
            size="small" 
            variant="tonal"
          >
            {{ item.resolved ? $t('admin.resolved') : $t('admin.unresolved') }}
          </v-chip>
        </template>

        <!-- Date -->
        <template v-slot:item.createdAt="{ item }">
          <div>{{ formatDate(item.createdAt) }}</div>
          <div class="text-caption text-grey">{{ formatTime(item.createdAt) }}</div>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex ga-1">
            <v-btn 
              icon 
              size="small" 
              variant="text"
              color="info"
              @click="showErrorDetail(item)"
            >
              <v-icon>mdi-eye</v-icon>
              <v-tooltip activator="parent" location="top">{{ $t('common.view') }}</v-tooltip>
            </v-btn>
            <v-btn 
              v-if="!item.resolved"
              icon 
              size="small" 
              variant="text"
              color="success"
              @click="resolveError(item)"
            >
              <v-icon>mdi-check</v-icon>
              <v-tooltip activator="parent" location="top">{{ $t('admin.resolve') }}</v-tooltip>
            </v-btn>
            <v-btn 
              icon 
              size="small" 
              variant="text"
              color="error"
              @click="confirmDelete(item)"
            >
              <v-icon>mdi-delete</v-icon>
              <v-tooltip activator="parent" location="top">{{ $t('common.delete') }}</v-tooltip>
            </v-btn>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="text-center pa-8">
            <v-icon size="64" color="grey">mdi-check-circle-outline</v-icon>
            <p class="text-h6 mt-4 text-grey">{{ $t('admin.noErrors') }}</p>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <v-card-actions class="justify-center pb-4">
        <v-pagination
          v-model="pagination.page"
          :length="pagination.totalPages"
          :total-visible="5"
          @update:model-value="loadErrors"
        />
      </v-card-actions>
    </v-card>

    <!-- Error Detail Dialog -->
    <v-dialog v-model="showDetailDialog" max-width="800">
      <v-card v-if="selectedError">
        <v-card-title class="bg-primary">
          <v-icon class="mr-2">mdi-bug</v-icon>
          {{ $t('admin.errorDetails') }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">{{ $t('admin.errorType') }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-1">
                    <v-chip :color="getErrorTypeColor(selectedError.errorType)" size="small">
                      {{ selectedError.errorType }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">{{ $t('admin.errorCode') }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-1">{{ selectedError.errorCode || '—' }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">{{ $t('admin.user') }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-1">
                    {{ selectedError.userName || 'Unknown' }} ({{ selectedError.userPhone || '�' }})
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">{{ $t('admin.date') }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-1">{{ formatDateTime(selectedError.createdAt) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">URL</v-list-item-title>
                  <v-list-item-subtitle class="text-body-1" style="word-break: break-all;">{{ selectedError.url || '—' }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">{{ $t('admin.method') }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-1">{{ selectedError.method || '—' }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">IP</v-list-item-title>
                  <v-list-item-subtitle class="text-body-1">{{ selectedError.ipAddress || '—' }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <div class="mb-4">
            <h4 class="text-subtitle-2 text-grey mb-2">{{ $t('admin.message') }}</h4>
            <v-sheet rounded class="pa-3 bg-grey-darken-3">
              <pre class="text-body-2" style="white-space: pre-wrap; word-break: break-word;">{{ selectedError.message }}</pre>
            </v-sheet>
          </div>

          <div v-if="selectedError.stack" class="mb-4">
            <h4 class="text-subtitle-2 text-grey mb-2">Stack Trace</h4>
            <v-sheet rounded class="pa-3 bg-grey-darken-4" style="max-height: 200px; overflow-y: auto;">
              <pre class="text-caption" style="white-space: pre-wrap; word-break: break-word;">{{ selectedError.stack }}</pre>
            </v-sheet>
          </div>

          <div v-if="selectedError.userAgent" class="mb-4">
            <h4 class="text-subtitle-2 text-grey mb-2">User Agent</h4>
            <v-sheet rounded class="pa-3 bg-grey-darken-3">
              <pre class="text-caption" style="white-space: pre-wrap; word-break: break-word;">{{ selectedError.userAgent }}</pre>
            </v-sheet>
          </div>

          <!-- Resolve Form -->
          <v-divider class="my-4" />
          <v-textarea
            v-model="resolveNotes"
            :label="$t('common.notes')"
            variant="outlined"
            rows="2"
            :placeholder="$t('admin.addNotes')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showDetailDialog = false">
            {{ $t('common.close') }}
          </v-btn>
          <v-btn 
            v-if="!selectedError.resolved"
            color="success" 
            variant="flat"
            @click="resolveErrorFromDialog"
          >
            <v-icon left>mdi-check</v-icon>
            {{ $t('admin.resolve') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error">
          <v-icon class="mr-2">mdi-delete-alert</v-icon>
          {{ $t('admin.confirmDelete') }}
        </v-card-title>
        <v-card-text class="pa-4">
          {{ $t('admin.deleteErrorConfirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showDeleteDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="error" variant="flat" @click="deleteError">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Clear All Confirm Dialog -->
    <v-dialog v-model="showClearAllDialog" max-width="450">
      <v-card>
        <v-card-title class="bg-error">
          <v-icon class="mr-2">mdi-delete-sweep</v-icon>
          {{ $t('admin.clearAllLogs') }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="warning" variant="tonal" class="mb-3">
            {{ $t('admin.clearAllWarning', { count: stats.total }) }}
          </v-alert>
          {{ $t('admin.clearAllConfirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showClearAllDialog = false" :disabled="clearingAll">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="error" variant="flat" @click="clearAllErrors" :loading="clearingAll">
            <v-icon left>mdi-delete-sweep</v-icon>
            {{ $t('admin.yesDeleteAll') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

const getToken = () => sessionStorage.getItem('korkem-auto-token') || localStorage.getItem('korkem-auto-token')

// State
const loading = ref(false)
const errors = ref([])
const selectedErrors = ref([])
const selectedError = ref(null)
const showDetailDialog = ref(false)
const showDeleteDialog = ref(false)
const showClearAllDialog = ref(false)
const clearingAll = ref(false)
const errorToDelete = ref(null)
const resolveNotes = ref('')

const stats = reactive({
  total: 0,
  unresolved: 0,
  today: 0,
  thisWeek: 0
})

const filters = reactive({
  errorType: null,
  resolved: null,
  search: ''
})

const pagination = reactive({
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0
})

const snackbar = reactive({
  show: false,
  message: '',
  color: 'success'
})

// Options
const errorTypes = computed(() => [
  { title: t('admin.errorTypes.api'), value: 'API_ERROR' },
  { title: t('admin.errorTypes.client'), value: 'CLIENT_ERROR' },
  { title: t('admin.errorTypes.auth'), value: 'AUTH_ERROR' },
  { title: t('admin.errorTypes.test'), value: 'TEST_ERROR' },
  { title: t('admin.errorTypes.network'), value: 'NETWORK_ERROR' }
])

const resolvedOptions = computed(() => [
  { title: t('admin.unresolved'), value: 'false' },
  { title: t('admin.resolved'), value: 'true' }
])

const headers = computed(() => [
  { title: 'ID', key: 'id', width: '80px' },
  { title: t('admin.errorType'), key: 'errorType', width: '140px' },
  { title: t('admin.user'), key: 'user', width: '180px' },
  { title: t('admin.message'), key: 'message' },
  { title: t('admin.url'), key: 'url', width: '200px' },
  { title: t('admin.status'), key: 'resolved', width: '120px' },
  { title: t('admin.date'), key: 'createdAt', width: '120px' },
  { title: t('common.actions'), key: 'actions', sortable: false, width: '120px' }
])

// Methods
const loadErrors = async () => {
  loading.value = true
  try {
    const token = getToken()
    console.log('🔐 ErrorLogs token check:', { tokenExists: !!token, tokenLength: token?.length })
    
    if (!token || token === 'null' || token === 'undefined') {
      console.error('❌ No valid token found')
      showMessage(t('admin.authRequired'), 'error')
      return
    }
    
    const params = {
      page: pagination.page,
      limit: pagination.limit
    }
    
    if (filters.errorType) params.errorType = filters.errorType
    if (filters.resolved !== null) params.resolved = filters.resolved
    if (filters.search) params.search = filters.search

    const response = await axios.get(`${API_URL}/errors`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    })

    if (response.data.success) {
      errors.value = response.data.data
      pagination.total = response.data.pagination.total
      pagination.totalPages = response.data.pagination.totalPages
      
      if (response.data.stats) {
        stats.unresolved = response.data.stats.unresolvedCount || 0
      }
    }

    // Load stats
    await loadStats()
  } catch (error) {
    console.error('Error loading errors:', error)
    showMessage(t('admin.loadErrorsFailed'), 'error')
  } finally {
    loading.value = false
  }
}

const loadStats = async () => {
  try {
    const token = getToken()
    const response = await axios.get(`${API_URL}/errors/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    if (response.data.success) {
      stats.total = response.data.stats.total || 0
      stats.unresolved = response.data.stats.unresolved || 0
      stats.today = response.data.stats.today || 0
      stats.thisWeek = response.data.stats.thisWeek || 0
    }
  } catch (error) {
    console.error('Error loading stats:', error)
  }
}

const showErrorDetail = (error) => {
  selectedError.value = error
  resolveNotes.value = error.notes || ''
  showDetailDialog.value = true
}

const resolveError = async (error) => {
  try {
    const token = getToken()
    await axios.put(
      `${API_URL}/errors/${error.id}/resolve`,
      { notes: '' },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    
    showMessage(t('admin.errorResolved'), 'success')
    loadErrors()
  } catch (error) {
    console.error('Error resolving:', error)
    showMessage(t('admin.resolveErrorFailed'), 'error')
  }
}

const resolveErrorFromDialog = async () => {
  try {
    const token = getToken()
    await axios.put(
      `${API_URL}/errors/${selectedError.value.id}/resolve`,
      { notes: resolveNotes.value },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    
    showDetailDialog.value = false
    showMessage(t('admin.errorResolved'), 'success')
    loadErrors()
  } catch (error) {
    console.error('Error resolving:', error)
    showMessage(t('admin.resolveErrorFailed'), 'error')
  }
}

const resolveSelected = async () => {
  if (selectedErrors.value.length === 0) return
  
  try {
    const token = getToken()
    await axios.put(
      `${API_URL}/errors/resolve-multiple`,
      { ids: selectedErrors.value },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    
    selectedErrors.value = []
    showMessage(t('admin.selectedErrorsResolved'), 'success')
    loadErrors()
  } catch (error) {
    console.error('Error resolving multiple:', error)
    showMessage(t('admin.resolveErrorsFailed'), 'error')
  }
}

const confirmDelete = (error) => {
  errorToDelete.value = error
  showDeleteDialog.value = true
}

const deleteError = async () => {
  try {
    const token = getToken()
    await axios.delete(`${API_URL}/errors/${errorToDelete.value.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    
    showDeleteDialog.value = false
    showMessage(t('admin.errorDeleted'), 'success')
    loadErrors()
  } catch (error) {
    console.error('Error deleting:', error)
    showMessage(t('admin.deleteErrorFailed'), 'error')
  }
}

const clearAllErrors = async () => {
  clearingAll.value = true
  try {
    const token = getToken()
    const response = await axios.delete(`${API_URL}/errors`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { olderThanDays: 0, resolvedOnly: false }
    })
    
    showClearAllDialog.value = false
    showMessage(t('admin.logsCleared', { count: response.data.count || 0 }), 'success')
    selectedErrors.value = []
    loadErrors()
  } catch (error) {
    console.error('Error clearing all logs:', error)
    showMessage(t('admin.clearLogsFailed'), 'error')
  } finally {
    clearingAll.value = false
  }
}

// Helpers
const getErrorTypeColor = (type) => {
  const colors = {
    'API_ERROR': 'error',
    'CLIENT_ERROR': 'warning',
    'AUTH_ERROR': 'purple',
    'TEST_ERROR': 'info',
    'NETWORK_ERROR': 'grey'
  }
  return colors[type] || 'grey'
}

const getErrorTypeIcon = (type) => {
  const icons = {
    'API_ERROR': 'mdi-server-off',
    'CLIENT_ERROR': 'mdi-bug',
    'AUTH_ERROR': 'mdi-account-alert',
    'TEST_ERROR': 'mdi-clipboard-alert',
    'NETWORK_ERROR': 'mdi-wifi-off'
  }
  return icons[type] || 'mdi-alert'
}

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('kk-KZ')
}

const formatTime = (dateStr) => {
  return new Date(dateStr).toLocaleTimeString('kk-KZ', { hour: '2-digit', minute: '2-digit' })
}

const formatDateTime = (dateStr) => {
  return new Date(dateStr).toLocaleString('kk-KZ')
}

const showMessage = (message, color = 'success') => {
  snackbar.message = message
  snackbar.color = color
  snackbar.show = true
}

// Lifecycle
onMounted(() => {
  loadErrors()
})
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
